// src/components/DashboardTabs/SocialTab.jsx
import { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { api } from "../../services/api";

function SocialTab({ band, canEdit = true, onSaved, exposeSave }) {
  // Somelinkit
  const [form, setForm] = useState({
    facebook:  band?.facebook  ?? band?.socials?.facebook  ?? "",
    instagram: band?.instagram ?? band?.socials?.instagram ?? "",
    tiktok:    band?.tiktok    ?? band?.socials?.tiktok    ?? "",
    youtube:   band?.youtube   ?? band?.socials?.youtube   ?? "",
  });
  const [saving, setSaving] = useState(false);

  const socialsFromBand = useMemo(() => ({
    facebook:  band?.facebook  ?? band?.socials?.facebook  ?? "",
    instagram: band?.instagram ?? band?.socials?.instagram ?? "",
    tiktok:    band?.tiktok    ?? band?.socials?.tiktok    ?? "",
    youtube:   band?.youtube   ?? band?.socials?.youtube   ?? "",
  }), [
    band?.facebook, band?.socials?.facebook,
    band?.instagram, band?.socials?.instagram,
    band?.tiktok, band?.socials?.tiktok,
    band?.youtube, band?.socials?.youtube,
  ]);
  useEffect(() => { setForm(socialsFromBand); }, [socialsFromBand]);

  // Korvaa aiemman normalizeUrlin: tukee @handle- ja domain-syötteet
  const normalizeSocialUrl = (service, v) => {
    let s = String(v || "").trim();
    if (!s) return "";
    // jos jo http(s) -> ok
    if (/^https?:\/\//i.test(s)) return s;
    // poista mahdolliset palveludomainit ilman protokollaa
    s = s
      .replace(/^((www\.)?instagram\.com\/@?)/i, "")
      .replace(/^((www\.)?tiktok\.com\/@?)/i, "")
      .replace(/^((www\.)?facebook\.com\/)/i, "")
      .replace(/^((www\.)?youtube\.com\/)/i, "")
      .replace(/^@+/, "");
    switch (service) {
      case "instagram": return `https://instagram.com/${s}`;
      case "tiktok":    return `https://tiktok.com/@${s}`;
      case "facebook":  return `https://facebook.com/${s}`;
      case "youtube":
        return /^(@|channel\/|c\/|user\/)/i.test(s)
          ? `https://youtube.com/${s.replace(/^@?/, "@")}`
          : `https://youtube.com/@${s}`;
      default:          return `https://${s}`;
    }
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const doSave = useCallback(async () => {
    if (!canEdit || saving) return;
    setSaving(true);
    try {
    const payload = {
      facebook:  form.facebook  ? normalizeSocialUrl("facebook",  form.facebook)  : "",
      instagram: form.instagram ? normalizeSocialUrl("instagram", form.instagram) : "",
      tiktok:    form.tiktok    ? normalizeSocialUrl("tiktok",    form.tiktok)    : "",
      youtube:   form.youtube   ? normalizeSocialUrl("youtube",   form.youtube)   : "",
    };
    // HUOM: oma reitti someille – ei { socials: payload }
    await api.updateBandSocials(band.id, payload);
      setForm(payload); // pitää kentät näkyvissä heti tallennuksen jälkeen
      toast.success("Some-linkit tallennettu.", { containerId: "global" });
      onSaved?.(payload);
    } catch (e) {
      console.error(e);
      toast.error("Tallennus epäonnistui.", { containerId: "global" });
    } finally {
      setSaving(false);
    }
  }, [canEdit, saving, form, band?.id, onSaved]);

  useEffect(() => {
    if (typeof exposeSave === "function") exposeSave(doSave);
    return () => { if (typeof exposeSave === "function") exposeSave(null); };
  }, [doSave, exposeSave]);

  const label = "block dark:text-white/90 text-black/90 font-customFont2 text-lg mb-1 font-bold";
  const input = "w-full h-10 rounded-lg px-3 bg-white dark:bg-black/40 border border-black/10 dark:border-white/10";
  const disabled = !canEdit || saving;

  return (
    <form
      className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4"
      onSubmit={(e) => e.preventDefault()} // ei omaa submitiä
    >
      <div>
        <label className={label}>Facebook</label>
        <input className={input} name="facebook" value={form.facebook} onChange={onChange} placeholder="facebook.com/…" disabled={disabled} inputMode="url" />
      </div>

      <div>
        <label className={label}>Instagram</label>
        <input className={input} name="instagram" value={form.instagram} onChange={onChange} placeholder="instagram.com/…" disabled={disabled} inputMode="url" />
      </div>

      <div>
        <label className={label}>TikTok</label>
        <input className={input} name="tiktok" value={form.tiktok} onChange={onChange} placeholder="tiktok.com/@…" disabled={disabled} inputMode="url" />
      </div>

      <div>
        <label className={label}>YouTube</label>
        <input className={input} name="youtube" value={form.youtube} onChange={onChange} placeholder="youtube.com/@… tai kanavalinkki" disabled={disabled} inputMode="url" />
      </div>
    </form>
  );
}

SocialTab.propTypes = {
  band: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    socials: PropTypes.object,
    facebook: PropTypes.string,
    instagram: PropTypes.string,
    tiktok: PropTypes.string,
    youtube: PropTypes.string,
  }).isRequired,
  canEdit: PropTypes.bool,
  onSaved: PropTypes.func,
  exposeSave: PropTypes.func,
};

export default SocialTab;
